import { Dispatch } from "redux";
import axios from "axios";

import { ActionType } from "../action-types";
import { Action } from "../actions";
import { Conversation } from "@twilio/conversations";
import { http } from "../../utils/http";
import {
  ConversationDto,
  ConversationDtoPagedResult,
} from "../../dto/conversation";
import { conversations } from "../../mocks/mock";
import { USE_MOCKS_DATA } from "../../utils/localUtils";
import { ConversationsState } from "../reducers/conversationsReducer";
import { AppThunk } from "./types";
import { addNotifications } from "./notifications";
import { NOTIFICATION_TIMEOUT } from "../../constants";
import { AppState } from "../reducers";

export const upsertConversation = (convo: Conversation) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.UPSERT_CONVERSATION,
      payload: convo,
    });
  };
};

export const removeConversation = (sid: string) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.REMOVE_CONVERSATION,
      payload: sid,
    });
  };
};

export const setReadConversation =
  (twilioSid: string): AppThunk<Promise<void>> =>
  async (_dispatch, getState) => {
    const jwt = getState().token;
    if (!jwt) throw new Error("Missing JWT!");

    const convState = getState().conversations;
    const dto = convState.items.find((c) => c.twilioSid === twilioSid);

    if (!dto) {
      console.warn(
        "[setReadConversation] Nessuna ConversationDto trovata per twilioSid:",
        twilioSid
      );
      return;
    }

    const conversationId = dto.id;

    try {
      await http.put(`/api/Conversations/${conversationId}/set-read`);
      await _dispatch(refreshConversationById(dto.id));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");
        throw new Error(
          `PUT /api/Conversations/${conversationId}/set-read failed REQUEST: ${status} ${text}`
        );
      }
      throw error;
    }
  };

export const updateCurrentConversation =
  (sid: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: ActionType.UPDATE_CURRENT_CONVERSATION,
        payload: sid,
      });

      await setReadConversation(sid)(dispatch, getState, null);
    } catch (e) {
      addNotifications([
        {
          variant: "error",
          message: `Set-Read Error ${sid}`,
          id: new Date().getTime(),
          dismissAfter: 2500,
        },
      ])(dispatch);
      console.error("[updateCurrentConversation] setReadConversation error", e);
    }
  };

export const setLastReadIndex = (index: number) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.CONVERSATION_LAST_READ_INDEX,
      payload: index,
    });
  };
};

export const updateLoadingState = (loadingStatus: boolean) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.UPDATE_LOADING_STATE,
      payload: loadingStatus,
    });
  };
};

export const updateUnreadMessages = (
  channelSid: string,
  unreadCount: number
) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.UPDATE_UNREAD_MESSAGES,
      payload: { channelSid, unreadCount },
    });
  };
};

export const updateConversation = (
  channelSid: string,
  parameters: Partial<Conversation>
) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.UPDATE_CONVERSATION,
      payload: { channelSid, parameters },
    });
  };
};

export const filterConversations = (searchString: string) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.FILTER_CONVERSATIONS,
      payload: searchString,
    });
  };
};

export const setConversations = (items: ConversationsState) => ({
  type: ActionType.SET_CONVERSATIONS,
  payload: items,
});

export const newFilterConversations =
  (query: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch({
      type: ActionType.NEW_FILTER_CONVERSATIONS,
      payload: query,
    });

    await dispatch(
      getConversations({
        q: query || undefined,
        page: 1,
      })
    );
  };

export const loadMoreConversations =
  (): AppThunk<Promise<void>> => async (dispatch, getState) => {
    const jwt = getState().token;
    if (!jwt) throw new Error("Missing JWT!");

    const convState = getState().conversations;
    const { page, pageSize, query, items } = convState;

    const nextPage = (page || 1) + 1;

    try {
      const res = await http.get("/api/Conversations", {
        params: {
          page: nextPage,
          pageSize: pageSize || 25,
          q: query || undefined,
        },
      });

      const data: ConversationDtoPagedResult = res.data;
      const newItems = data.items ?? [];

      if (newItems.length === 0) return;

      const merged = [...items, ...newItems];

      dispatch(
        setConversations({
          items: merged,
          page: data.page ?? nextPage,
          pageSize: data.pageSize ?? (pageSize || 25),
          totalItems:
            (data as any).totalItems ??
            (data as any).totalCount ??
            merged.length,
          query: query ?? "",
        })
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");
        throw new Error(
          `GET /api/Conversations (loadMore) failed REQUEST: ${status} ${text}`
        );
      }
      throw error;
    }
  };

export const getConversations =
  (params?: {
    state?: string;
    q?: string;
    page?: number;
    pageSize?: number;
  }): AppThunk<Promise<ConversationDtoPagedResult>> =>
  async (dispatch, getState) => {
    const jwt = getState().token;
    if (!jwt) throw new Error("Missing JWT!");

    const convState = getState().conversations;
    const page = params?.page ?? convState.page ?? 1;
    const pageSize = params?.pageSize ?? convState.pageSize ?? 25;
    const q = params?.q ?? (convState.query || undefined);

    try {
      const res = await http.get("/api/Conversations", {
        params: {
          ...params,
          page,
          pageSize,
          q,
        },
      });

      const data: ConversationDtoPagedResult = res.data;
      const items = data.items ?? [];

      if (USE_MOCKS_DATA) {
        dispatch(
          setConversations({
            items: conversations,
            page: 1,
            pageSize: conversations.length,
            totalItems: conversations.length,
            query: q ?? "",
          })
        );
      } else {
        dispatch(
          setConversations({
            items,
            page: data.page ?? page,
            pageSize: data.pageSize ?? pageSize,
            totalItems:
              (data as any).totalItems ??
              (data as any).totalCount ??
              items.length,
            query: q ?? "",
          })
        );
      }

      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");
        throw new Error(
          `GET /api/Conversations failed REQUEST: ${status} ${text}`
        );
      }
      throw error;
    }
  };

export const refreshConversationById =
  (conversationId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const jwt = getState().token;
    if (!jwt) throw new Error("Missing JWT!");

    try {
      const res = await http.get(`/api/Conversations/${conversationId}`);
      const updated = res.data as ConversationDto;

      const convState = (getState() as AppState).conversations;
      const items = convState.items ?? [];

      const idx = items.findIndex((c) => c.id === conversationId);
      let newItems: ConversationDto[];

      if (idx === -1) {
        // se non c’è ancora, la aggiungo in testa
        newItems = [updated, ...items];
      } else {
        newItems = [...items];
        newItems[idx] = updated;
      }

      dispatch(
        setConversations({
          items: newItems,
          page: convState.page,
          pageSize: convState.pageSize,
          totalItems: convState.totalItems,
          query: convState.query,
        })
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");

        throw new Error(
          `GET /api/Conversations/${conversationId} failed REQUEST: ${status} ${text}`
        );
      }
      throw error;
    }
  };
